import React, { useContext, useEffect, useRef, useState } from 'react';
import { CountryCode, CountryFlagForCode, CountryFlagForCodeKey, CountryName, CountryNameKey, CurrencyCode } from '@dayetopia/types';
import { asHTML } from '@prismicio/helpers';
import { useLocation } from '@reach/router';
import styled, { keyframes } from 'styled-components';
import { Context } from '@components/Context';
import { NavContext } from '@components/navigation/Context';
import Colors from '@components/shared/Colors';
import TimeCountdown from '@components/shared/TimeCountdown';
import Trustpilot from '@components/shared/Trustpilot';
import { holidayCutoffDates } from '@constants';
import { NavStatus, Promotion, SiteBannerData } from '@contracts';
import { getSiteBannerData } from '@data/siteBanner';
import { showInCountryCondition } from '@utils/country';
import { pageIsFocusPage } from '@utils/helpers';
import { removeLocalesFromPath } from '@utils/language';
import { selectPromotionTracking, viewPromotionTracking } from '@utils/tracking';
import useNavigate from '@utils/useNavigate';
import FadeIn from './FadeIn';

const SiteBanner: React.FC = () => {
  const { state } = useContext(Context);
  const { navState, navDispatch } = useContext(NavContext);
  const { navigateWrapper } = useNavigate();
  const { currency, country, loggedIn, firebaseAuth } = state;
  const { status } = navState;
  const bannerRef = useRef(null);
  const location = useLocation();
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const banners: [SiteBannerData] = getSiteBannerData();
  const [siteBanner, setSiteBanner] = useState<SiteBannerData | null>(null);
  const countryName = CountryName[country as CountryNameKey];
  const countryFlag = CountryFlagForCode[country as CountryFlagForCodeKey];
  const isFocusPage = pageIsFocusPage(location.pathname);
  const hideBanner = !location || location.pathname.includes('/dashboard/' || '/account/') || isFocusPage;

  useEffect(() => {
    const getDimensions = () => ({
      width: (bannerRef && bannerRef.current?.offsetWidth) || 0,
      height: (bannerRef && bannerRef.current?.offsetHeight) || 0
    });

    const handleResize = () => {
      setDimensions(getDimensions());
    };

    if (bannerRef.current) {
      setDimensions(getDimensions());
    }

    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [bannerRef]);

  useEffect(() => {
    if (hideBanner || !firebaseAuth) return;

    setBannerConfig();
  }, [hideBanner, country, firebaseAuth]);

  const setBannerConfig = () => {
    let showBanner = false;
    const sortedBanners = banners.sort(({ priority: a }, { priority: b }) => (a ?? 99) - (b ?? 99));
    for (let i = 0; i < sortedBanners.length; i++) {
      const banner = sortedBanners[i];
      const allConditionsMet = checkBannerRequirements(banner);
      if (allConditionsMet) {
        setSiteBanner(banner);
        showBanner = true;
        if (banner.promotion_id) {
          const bannerText = banner.banner_text?.richText.length > 0 ? banner.banner_text?.richText[0]?.text : '';
          const promotion = {
            promotion_id: banner.promotion_id?.text,
            creative: bannerText,
            name: banner.promotion_name?.text,
            position: 'top_banner',
            products: []
          };
          const userEmail = firebaseAuth?.currentUser?.email ?? '';
          viewPromotionTracking([promotion], userEmail);
        }
        break;
      }
    }

    navDispatch({ type: 'setShowingBanner', data: showBanner });
  };

  const checkBannerRequirements = (banner: SiteBannerData) => {
    const checkRequiresLoggedIn = banner.logged_in;
    if (checkRequiresLoggedIn && loggedIn) {
      return false;
    }

    if (banner.page_path_includes.text) {
      const pageIncludes = banner.page_path_includes.text?.split(',') ?? [];
      if (!pageIncludes.includes(removeLocalesFromPath(location.pathname))) {
        return false;
      }
    }

    if (banner.page_path_excludes.text) {
      const pageExcludes = banner.page_path_excludes.text?.split(',') ?? [];
      if (pageExcludes.includes(removeLocalesFromPath(location.pathname))) {
        return false;
      }
    }

    if (banner.country_code.text) {
      const showInCountry = showInCountryCondition(banner.country_code.text, country);
      if (!showInCountry) {
        return false;
      }
    }
    return true;
  };

  if (hideBanner) return null;

  if (!siteBanner) return <Container $backgroundColor={Colors.sage}></Container>;

  const hasLink = siteBanner.banner_link?.text ?? false;

  const onClickHandler = () => {
    if (siteBanner.promotion_id) {
      const promotion = getPromotionItem();
      const userEmail = firebaseAuth?.currentUser?.email ?? '';
      selectPromotionTracking([promotion], userEmail);
    }

    let link = siteBanner.banner_link?.text ?? '';

    const urlReplacements = {
      '/products/at-home-vaginal-screening/': '/products/vaginal-testing/'
    };

    const shouldReplaceUrl = country === CountryCode.US && urlReplacements[link];
    link = shouldReplaceUrl ? urlReplacements[link] : link;

    if (siteBanner.new_window) {
      window.open(link);
    } else {
      navigateWrapper(link);
    }
  };

  const getPromotionItem = () => {
    const bannerText = siteBanner.banner_text?.richText.length > 0 ? siteBanner.banner_text?.richText[0]?.text : '';
    const promotion: Promotion = {
      promotion_id: siteBanner.promotion_id?.text,
      name: siteBanner.promotion_name?.text,
      creative: bannerText,
      position: 'top_banner',
      products: []
    };
    return promotion;
  };

  const getBannerHTML = () => {
    let renderHTML = asHTML(siteBanner.banner_text?.richText);
    renderHTML = renderHTML.replace('{{country}}', `${countryName} ${countryFlag}`);
    if (renderHTML.includes('{{date}}')) {
      const cutoffDate = holidayCutoffDates[currency.letterCode];
      renderHTML = renderHTML.replace('{{date}}', cutoffDate);
    }
    if (renderHTML.includes('{{countryCode}}')) {
      const countryCode = currency.letterCode === CurrencyCode.EUR ? 'EU' : 'UK';
      renderHTML = renderHTML.replace('{{countryCode}}', countryCode);
    }
    return renderHTML;
  };

  const renderBannerContent = () => (
    <BannerTextContainer
      $textColor={siteBanner.text_color}
      $highlightBackgroundColor={siteBanner.highlight_background_color}
      $highlightTextColor={siteBanner.highlight_text_color}
      $counterBackgroundColor={siteBanner.counter_background_color}
      $counterTextColor={siteBanner.counter_text_color}
    >
      {siteBanner.counter_date && <TimeCountdown date={siteBanner.counter_date} />}
      <div dangerouslySetInnerHTML={{ __html: getBannerHTML() }} />
      {siteBanner.trustpilot && (
        <TrustpilotWrap>
          <Trustpilot justifyContent="start" showFallback={false} />
        </TrustpilotWrap>
      )}
    </BannerTextContainer>
  );

  return (
    <Container $backgroundColor={siteBanner.background_color} $isOnTop={status !== NavStatus.Basket}>
      <FadeIn duration={200}>
        <Inner>
          {hasLink && <OverlayLinkStyled onClick={onClickHandler} id={siteBanner?.google_analytics_id?.text} />}
          <BannerContent ref={bannerRef}>{renderBannerContent()}</BannerContent>
          <BannerContentDuplicate width={dimensions.width}>{renderBannerContent()}</BannerContentDuplicate>
        </Inner>
      </FadeIn>
    </Container>
  );
};

export default SiteBanner;

const slideshowMove = keyframes`
  100% {
    transform: translate3d(-100%, 0, 0)
  }
`;

interface StyleProps {
  $backgroundColor?: string;
  $textColor?: string;
  $highlightBackgroundColor?: string;
  $highlightTextColor?: string;
  $counterBackgroundColor?: string;
  $counterTextColor?: string;
  $isOnTop?: boolean;
}

const Container = styled.div<StyleProps>`
  background: ${(props) => (props.$backgroundColor ? `${props.$backgroundColor}` : Colors.cream)};
  z-index: ${(props) => (props.$isOnTop ? 101 : 'auto')};
  border-bottom: 1px solid ${Colors.cream};
  padding: 13px 0;
  height: 50px;
  position: relative;

  @media only screen and (max-width: 1024px) {
    overflow: hidden;
  }
`;

const Inner = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  text-align: center;
  z-index: 1;
`;

const BannerTextContainer = styled.div<StyleProps>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0;
  gap: 10px;

  p {
    color: ${(props) => (props.$textColor ? `${props.$textColor}` : Colors.forestGreen)};
    font-weight: 700;
    font-size: 13px;
    line-height: 18px;
    letter-spacing: 1.5px;
    text-transform: uppercase;

    span {
      background-color: ${(props) => (props.$highlightBackgroundColor ? `${props.$highlightBackgroundColor}` : Colors.forestGreen)};
      color: ${(props) => (props.$highlightTextColor ? `${props.$highlightTextColor}` : Colors.cream)};
      border-radius: 3px;
      padding: 3px 6px;
      margin: 0 2px;
      display: inline-block;
    }
  }

  span.countdown-digit {
    font-family: 'BrownStd';
    font-weight: 700;
    min-width: 17px;
    font-size: 13px;
    line-height: 18px;
    text-align: center;
    text-transform: uppercase;
    color: ${(props) => (props.$textColor ? `${props.$textColor}` : Colors.forestGreen)};
  }

  span.countdown-digit-number {
    padding: 3px 5px;
    gap: 10px;
    height: 24px;
    background-color: ${(props) => (props.$counterBackgroundColor ? `${props.$counterBackgroundColor}` : Colors.cream)};
    color: ${(props) => (props.$counterTextColor ? `${props.$counterTextColor}` : Colors.forestGreen)};
    border-radius: 3px;
  }
`;

const TrustpilotWrap = styled.div`
  width: 250px;
  margin-left: -15px;
  height: 28px;

  & .trustpilot-container {
    padding: 0 !important;
  }
`;

const BannerContent = styled.div`
  padding: 0 20px;
  align-items: center;
  display: flex;
  white-space: nowrap;

  @media only screen and (max-width: 768px) {
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    text-align: center;
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    transform: translate3d(0, 0, 0);
    animation: ${slideshowMove} 16s linear infinite;

    > a + img {
      display: none;
    }
  }
`;

const BannerContentDuplicate = styled(BannerContent)`
  display: none;
  @media only screen and (max-width: 768px) {
    display: flex;
    left: ${({ width }) => `${width}`}px;
    animation: ${slideshowMove} 16s linear infinite;

    > a + img {
      display: none;
    }
  }
`;

const OverlayLinkStyled = styled.a`
  cursor: pointer;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3;
`;
